import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { FilePen, Plus } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { useDraftOrderColumns } from '../components/draft-order-columns';
import { draftOrderFilters } from '../components/draft-order-filters';
import { useDraftOrders } from '../hooks/use-draft-orders';

const ROUTE_ID = '/_authed/_active/s/$storeId/draft-orders/' as const;
const ROUTE_PATH = '/s/$storeId/draft-orders/' as const;

function NewDraftButton({ storeId, tour }: { storeId: string; tour?: boolean }) {
  const { t } = useTranslation('draftOrders');
  return (
    <Can permission="orders.write">
      <Button asChild data-tour={tour ? 'draft-orders.list.new' : undefined}>
        <Link to="/s/$storeId/draft-orders/new" params={{ storeId }}>
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );
}

function DraftOrders() {
  const { t } = useTranslation('draftOrders');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const queryClient = useQueryClient();

  const filters = useMemo(
    () => draftOrderFilters({ t, queryClient, storeId: store.id, currency: store.defaultCurrency }),
    [queryClient, store.defaultCurrency, store.id, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useDraftOrders(store.id, list.params);
  const columns = useDraftOrderColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'draft-orders',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={<NewDraftButton storeId={store.id} tour />}
      />
      {isEmptyList ? (
        <EmptyState
          icon={FilePen}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="draft-orders.list.empty"
          tourLink
          action={<NewDraftButton storeId={store.id} />}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/draft-orders/$draftId',
              params: { storeId: store.id, draftId: row.id },
            })
          }
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'draft-orders.list.table' }}
        />
      )}
    </PageContent>
  );
}

export function DraftOrdersPage() {
  return (
    <FeatureErrorBoundary name="draftOrders.list">
      <DraftOrders />
    </FeatureErrorBoundary>
  );
}
